import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Sparkle, ArrowsClockwise, CheckCircle, Archive, TrashSimple, Flask,
  Leaf, FloppyDisk, PencilSimple, X as IconX, ArrowSquareOut, DownloadSimple,
  CircleNotch, ShieldCheck, Image as ImageIcon, Eye, Copy, InstagramLogo,
  FilmReel,
} from "@phosphor-icons/react";
import {
  adminListDailyPosts, adminGenerateDaily, adminGenerateCustomDaily,
  adminGenerateBulkDaily, adminEditDailyPost, adminPublishDailyPost,
  adminArchiveDailyPost, adminDeleteDailyPost, adminExportDailyUrl,
  adminRegenerateDailyImage, adminStoryUrl, adminReelUrl,
} from "@/lib/api";
import MusicLibrary from "@/components/daily/MusicLibrary";

const STATUS_FILTERS = [
  { id: "", label: "Alle", color: "#A1A1AA" },
  { id: "draft", label: "Entwürfe", color: "#FFC24B" },
  { id: "published", label: "Veröffentlicht", color: "#00FA9A" },
  { id: "archived", label: "Archiv", color: "#8A8A93" },
];

function StatusBadge({ status }) {
  const map = {
    draft: { color: "#FFC24B", label: "Entwurf" },
    published: { color: "#00FA9A", label: "Live" },
    archived: { color: "#8A8A93", label: "Archiv" },
  };
  const meta = map[status] || map.draft;
  return (
    <span
      className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm text-white"
      style={{ background: meta.color }}
    >
      {meta.label}
    </span>
  );
}

function PreviewModal({ post, onClose }) {
  if (!post) return null;
  const accent = post.scientific ? "#8B7CF6" : "#7EE8B0";

  // Auto-pick relevant hashtags based on topic
  const tagsByTopic = {
    bienen: "#bienen #wildbienen #bestäuber #naturschutz",
    acker: "#landwirtschaft #ackerbau #ndvi #boden",
    weide: "#weide #pferd #tierhaltung #grünland",
    wald: "#wald #forst #klima #biodiversität",
    klima: "#klima #klimawandel #wetter",
    markt: "#agrarmärkte #weizen #raps #soja",
    mikroben: "#bodenleben #mikrobiom #regenerativelandwirtschaft",
    saatgut: "#saatgut #vielfalt #alteSorten",
    wasser: "#wasser #boden #klimaanpassung",
    wissenschaft: "#wissenschaft #research #ökologie",
    vision: "#bodenblick #naturwissen",
  };
  const hashtags = `${tagsByTopic[post.topic] || "#bodenblick"} #bodenblick #natur`;

  const buildCaption = () => {
    const lines = [
      post.title,
      "",
      post.teaser,
      "",
      post.body,
    ];
    if (post.source_url) {
      lines.push("");
      lines.push(`Quelle: ${post.source_title || "Externer Link"} (${post.source_url})`);
    }
    lines.push("");
    lines.push(hashtags);
    lines.push("");
    lines.push(post.legal_tail || "");
    return lines.join("\n");
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(buildCaption());
      toast.success("Caption + Hashtags kopiert", {
        description: `Bild liegt unter: ${window.location.origin}${post.image_url || "(noch ausstehend)"}`,
      });
    } catch (err) {
      toast.error("Konnte nicht kopieren — bitte Browser-Berechtigung prüfen.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="studio-preview-modal"
    >
      <div
        className="bg-[#121212] max-w-2xl w-full my-4 rounded-sm shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-2.5 bg-[#F5F5F5] text-[#0A0A0A] flex items-center justify-between gap-3">
          <span className="text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5">
            <Eye size={11} weight="fill" /> Live-Vorschau · so erscheint der Post auf /heute
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={adminStoryUrl(post.id)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="studio-preview-story"
              className="inline-flex items-center gap-1 text-[11px] bg-gradient-to-r from-[#FFC24B] via-[#C05A3E] to-[#3D2A78] hover:opacity-90 px-2.5 py-1 rounded-sm"
              title="1080×1920 Instagram-Story herunterladen"
            >
              <InstagramLogo size={11} weight="fill" /> Story
            </a>
            <a
              href={adminReelUrl(post.id)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="studio-preview-reel"
              className="inline-flex items-center gap-1 text-[11px] bg-gradient-to-r from-[#3D2A78] via-[#C05A3E] to-[#FFC24B] hover:opacity-90 px-2.5 py-1 rounded-sm"
              title="Reel mit Sprachausgabe + Hintergrundmusik"
            >
              <FilmReel size={11} weight="fill" /> Reel · Voice + Musik
            </a>
            <button
              onClick={copyCaption}
              data-testid="studio-preview-copy-caption"
              className="inline-flex items-center gap-1 text-[11px] bg-[#00FA9A] hover:bg-[#33FBAE] px-2.5 py-1 rounded-sm"
              title="Caption + Hashtags in die Zwischenablage kopieren — bereit für Insta / LinkedIn / Threads"
            >
              <Copy size={11} weight="fill" /> Caption kopieren
            </button>
            <button
              onClick={onClose}
              data-testid="studio-preview-close"
              className="text-white/70 hover:text-white"
            >
              <IconX size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div
          className="px-5 py-3 flex items-start gap-2 text-[11px] leading-snug bg-[#8B7CF6]/10 border-b border-[#8B7CF6]/40 text-[#A99AFF]"
        >
          <ShieldCheck size={14} weight="fill" className="shrink-0 mt-0.5" />
          <p>
            <strong>Hinweis (EU-KI-VO Art. 50):</strong> Alle Tagesbeiträge auf
            dieser Seite werden mit Hilfe generativer KI automatisch erstellt.
            Sie dienen ausschließlich zu Informationszwecken. Keine
            Handlungsempfehlung. Keine Beratung. Keine Haftung.
          </p>
        </div>
        <article style={{ borderTop: `3px solid ${accent}` }}>
          {post.image_url && (
            <div className="aspect-square w-full overflow-hidden bg-white/5">
              <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="px-5 pt-5 pb-2 flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm text-white inline-flex items-center gap-1"
              style={{ background: accent }}
            >
              {post.scientific
                ? <><Flask size={10} weight="fill" /> Wissenschaft</>
                : <><Leaf size={10} weight="fill" /> {post.topic_label}</>}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#8B7CF6]/10 text-[#A99AFF] border border-[#8B7CF6]/40 inline-flex items-center gap-1">
              <Sparkle size={10} weight="fill" /> KI-generiert
            </span>
          </div>
          <div className="px-5 pb-5">
            <h2 className="text-2xl font-medium tracking-tight">{post.title}</h2>
            {post.teaser && (
              <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{post.teaser}</p>
            )}
            <div className="mt-3 text-[15px] leading-relaxed text-white whitespace-pre-wrap">
              {post.body}
            </div>
            {post.source_url && (
              <div className="mt-4 text-xs text-zinc-400">
                Originalquelle:{" "}
                <a
                  href={post.source_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-[#00FA9A] hover:underline"
                >
                  {post.source_title || "Externer Link"}
                </a>
              </div>
            )}
            <div className="mt-5 pt-3 border-t border-white/10 text-[11px] text-zinc-500 leading-snug">
              {post.legal_tail}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

function PostRow({ post, onChange, onPreview }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    title: post.title, teaser: post.teaser, body: post.body,
  });
  const [busy, setBusy] = useState(false);

  const guard = async (action, label) => {
    setBusy(true);
    try {
      await action();
      toast.success(label);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="eid-card overflow-hidden"
      data-testid={`studio-post-${post.id}`}
    >
      <div className="px-5 py-3 flex items-center gap-2 flex-wrap border-b border-white/10">
        <StatusBadge status={post.status} />
        <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">
          {post.date_iso}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/5 text-white">
          {post.scientific
            ? <><Flask size={10} weight="fill" /> Wissenschaft</>
            : <><Leaf size={10} weight="fill" /> {post.topic_label}</>}
        </span>
        {post.source_url && (
          <a
            href={post.source_url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-[11px] text-[#00FA9A] hover:underline inline-flex items-center gap-1"
          >
            Quelle <ArrowSquareOut size={10} />
          </a>
        )}
        <div className="flex-1" />
        <span className="text-[10px] text-zinc-500 font-mono">
          {post.ai_model || "gemini"}
        </span>
      </div>

      <div className="px-5 py-4 flex flex-col md:flex-row gap-4">
        {(post.image_url || post.image_prompt) && (
          <div className="md:w-44 shrink-0">
            <div className="aspect-square w-full bg-white/5 rounded-sm overflow-hidden flex items-center justify-center" data-testid={`studio-thumb-${post.id}`}>
              {post.image_url ? (
                <img src={post.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider px-2 text-center">
                  Bild ausstehend
                </div>
              )}
            </div>
            {post.image_prompt && (
              <p className="text-[10px] text-zinc-500 mt-1.5 italic line-clamp-3" title={post.image_prompt}>
                Prompt: {post.image_prompt}
              </p>
            )}
            <button
              onClick={() => guard(() => adminRegenerateDailyImage(post.id), "Bild neu generiert")}
              disabled={busy}
              data-testid={`studio-regen-image-${post.id}`}
              className="mt-2 w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] border border-white/10 hover:bg-[#121212] rounded-sm"
            >
              <ImageIcon size={11} /> Bild neu
            </button>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {editing ? (
          <div className="space-y-2">
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full px-3 py-2 text-xl font-medium border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40"
              data-testid={`studio-edit-title-${post.id}`}
            />
            <input
              type="text"
              value={draft.teaser}
              onChange={(e) => setDraft({ ...draft, teaser: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40"
              data-testid={`studio-edit-teaser-${post.id}`}
            />
            <textarea
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              rows="10"
              className="w-full px-3 py-2 text-sm leading-relaxed border border-white/10 rounded-sm focus:outline-none focus:border-[#00FA9A]/40 font-sans"
              data-testid={`studio-edit-body-${post.id}`}
            />
            <div className="flex gap-2">
              <button
                onClick={() => guard(async () => {
                  await adminEditDailyPost(post.id, draft);
                  setEditing(false);
                }, "Änderungen gespeichert")}
                disabled={busy}
                data-testid={`studio-save-${post.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#00FA9A] hover:bg-[#33FBAE] text-white rounded-sm"
              >
                <FloppyDisk size={12} weight="fill" /> Speichern
              </button>
              <button
                onClick={() => { setDraft({ title: post.title, teaser: post.teaser, body: post.body }); setEditing(false); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-white/10 hover:bg-white/5 rounded-sm"
              >
                <IconX size={12} /> Abbrechen
              </button>
            </div>
          </div>
          ) : (
            <>
              <h2 className="text-xl md:text-2xl font-medium tracking-tight" data-testid={`studio-title-${post.id}`}>
                {post.title}
              </h2>
              <p className="text-sm text-zinc-400 mt-1.5">{post.teaser}</p>
              <div className="mt-3 text-[15px] leading-relaxed text-white whitespace-pre-wrap">
                {post.body}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-5 py-3 bg-[#0A0A0A] border-t border-white/10 flex flex-wrap gap-2">
        <button
          onClick={() => onPreview(post)}
          data-testid={`studio-preview-${post.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#121212] border border-[#8B7CF6]/40 text-[#A99AFF] hover:bg-[#8B7CF6]/10 rounded-sm"
        >
          <Eye size={12} weight="fill" /> Vorschau
        </button>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            disabled={busy}
            data-testid={`studio-edit-${post.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-white/10 hover:bg-[#121212] rounded-sm"
          >
            <PencilSimple size={12} /> Bearbeiten
          </button>
        )}
        {post.status !== "published" && (
          <button
            onClick={() => guard(() => adminPublishDailyPost(post.id), "Veröffentlicht")}
            disabled={busy}
            data-testid={`studio-publish-${post.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#00FA9A] hover:bg-[#33FBAE] text-white rounded-sm"
          >
            <CheckCircle size={12} weight="fill" /> Veröffentlichen
          </button>
        )}
        {post.status !== "archived" && (
          <button
            onClick={() => guard(() => adminArchiveDailyPost(post.id), "Archiviert")}
            disabled={busy}
            data-testid={`studio-archive-${post.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-white/10 hover:bg-[#121212] rounded-sm"
          >
            <Archive size={12} /> Archivieren
          </button>
        )}
        <a
          href={adminStoryUrl(post.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gradient-to-r from-[#FFC24B] via-[#C05A3E] to-[#3D2A78] text-white hover:opacity-90 rounded-sm"
          data-testid={`studio-story-${post.id}`}
          title="Instagram-Story (1080×1920 PNG) herunterladen — bereit zum Hochladen"
        >
          <InstagramLogo size={12} weight="fill" /> Story
        </a>
        <a
          href={adminReelUrl(post.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gradient-to-r from-[#3D2A78] via-[#C05A3E] to-[#FFC24B] text-white hover:opacity-90 rounded-sm"
          data-testid={`studio-reel-${post.id}`}
          title="Reel mit deutscher Sprachausgabe + sanfter Hintergrund­musik (1080×1920 MP4, 6-12 s, ~20 s Generierung)"
        >
          <FilmReel size={12} weight="fill" /> Reel · Voice + Musik
        </a>
        <a
          href={adminReelUrl(post.id, { music: false })}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#8B7CF6]/40 text-[#A99AFF] hover:bg-[#8B7CF6]/10 rounded-sm"
          data-testid={`studio-reel-voiceonly-${post.id}`}
          title="Reel mit Stimme, ohne Hintergrundmusik — eigene Musik im Insta-Editor wählen"
        >
          <FilmReel size={12} /> · nur Voice
        </a>
        <a
          href={adminReelUrl(post.id, { voice: false, music: false })}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#8B7CF6]/40 text-[#A99AFF] hover:bg-[#8B7CF6]/10 rounded-sm"
          data-testid={`studio-reel-silent-${post.id}`}
          title="Stummes Reel (5 s) — schnell, eigene Musik im Insta-Editor"
        >
          <FilmReel size={12} /> · still
        </a>
        <a
          href={adminExportDailyUrl(post.id, "md")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-white/10 hover:bg-[#121212] rounded-sm"
          data-testid={`studio-export-md-${post.id}`}
        >
          <DownloadSimple size={12} /> Markdown
        </a>
        <a
          href={adminExportDailyUrl(post.id, "html")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-white/10 hover:bg-[#121212] rounded-sm"
          data-testid={`studio-export-html-${post.id}`}
        >
          <DownloadSimple size={12} /> HTML
        </a>
        <div className="flex-1" />
        <button
          onClick={() => {
            if (window.confirm("Diesen Beitrag dauerhaft löschen?")) {
              guard(() => adminDeleteDailyPost(post.id), "Gelöscht");
            }
          }}
          disabled={busy}
          data-testid={`studio-delete-${post.id}`}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#FF5500]/40 text-[#FF7A45] hover:bg-[#FF5500]/10 rounded-sm"
        >
          <TrashSimple size={12} /> Löschen
        </button>
      </div>
    </article>
  );
}

export default function DailyStudio() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("draft");
  const [previewPost, setPreviewPost] = useState(null);
  const [batchCount, setBatchCount] = useState(4);
  const [customTopic, setCustomTopic] = useState("");
  const [customFmt, setCustomFmt] = useState("instagram");
  const [autoPublish, setAutoPublish] = useState(false);
  const [genCustom, setGenCustom] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTopics, setBulkTopics] = useState("");
  const [bulkFmt, setBulkFmt] = useState("instagram");
  const [bulkAuto, setBulkAuto] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      setPosts(await adminListDailyPosts(statusFilter));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await adminGenerateDaily(batchCount);
      if (res.skipped) {
        toast.info(`Heute existieren bereits ${res.existing} Beiträge.`);
      } else {
        toast.success(`${res.generated} neue Entwürfe für ${res.date} erstellt`);
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const generateBulk = async () => {
    const lines = bulkTopics
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length >= 3);
    if (lines.length === 0) {
      toast.error("Bitte mindestens ein Thema (≥3 Zeichen) eintragen.");
      return;
    }
    if (lines.length > 15) {
      toast.error("Maximal 15 Themen auf einmal — bitte Liste kürzen.");
      return;
    }
    setBulkRunning(true);
    try {
      const res = await adminGenerateBulkDaily(lines, bulkFmt, bulkAuto);
      let msg = `${res.generated} Beiträge erstellt`;
      if (res.image_jobs_queued > 0) {
        msg += ` · ${res.image_jobs_queued} Bilder werden generiert (~${res.image_jobs_queued * 15}s)`;
      }
      if (res.errors?.length) msg += ` · ${res.errors.length} Fehler`;
      if (res.skipped_due_to_cap?.length) {
        msg += ` · ${res.skipped_due_to_cap.length} übersprungen (Tageslimit)`;
      }
      toast.success(msg);
      setBulkTopics("");
      await reload();
    } catch (err) {
      const reason = err?.response?.data?.detail;
      const m =
        reason === "daily_cap_reached" ? "Tageslimit von 15 Beiträgen erreicht."
        : reason === "no_topics" ? "Keine gültigen Themen gefunden."
        : reason || "Bulk-Generierung fehlgeschlagen";
      toast.error(m);
    } finally {
      setBulkRunning(false);
    }
  };


  const generateCustom = async () => {
    const topic = customTopic.trim();
    if (topic.length < 3) {
      toast.error("Bitte ein Thema mit mind. 3 Zeichen eingeben.");
      return;
    }
    setGenCustom(true);
    try {
      const res = await adminGenerateCustomDaily(topic, customFmt, autoPublish);
      const isAuto = res.auto_publish && res.image_job_queued;
      toast.success(
        isAuto
          ? `„${res.post.title}" — wird nach Bild-Erstellung automatisch veröffentlicht (~15s)`
          : customFmt === "instagram"
          ? `Instagram-Post erstellt: „${res.post.title}"`
          : `Beitrag erstellt: „${res.post.title}"`
      );
      setCustomTopic("");
      await reload();
    } catch (err) {
      const reason = err?.response?.data?.detail;
      const msg =
        reason === "daily_cap_reached" ? "Tageslimit von 15 Beiträgen erreicht."
        : reason === "topic_text_too_short" ? "Thema zu kurz."
        : reason === "llm_failed" ? "KI-Generierung fehlgeschlagen — bitte erneut versuchen."
        : reason || "Generierung fehlgeschlagen";
      toast.error(msg);
    } finally {
      setGenCustom(false);
    }
  };

  const drafts = posts.filter((p) => p.status === "draft").length;
  const published = posts.filter((p) => p.status === "published").length;

  return (
    <div className="space-y-5" data-testid="daily-studio-root">
      <Helmet>
        <title>Content Studio · Heute · Bodenblick Admin</title>
      </Helmet>

      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="eid-label inline-flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" className="text-[#A99AFF]" />
            Content Studio · Heute
          </div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1.5">
            Tägliche Beiträge verwalten
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Bis zu 15 KI-generierte Tagesbeiträge aus 9 Themenbereichen + 1
            wissenschaftlicher Beitrag mit echter Forschungsquelle. Cron läuft
            täglich um 06:00. Du prüfst, bearbeitest und veröffentlichst manuell.
            Für Instagram: gib unten ein eigenes Thema vor und wähle „Instagram (kurz)".
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label
            className="flex items-center gap-2 text-xs text-zinc-400 bg-[#121212] px-2.5 py-1.5 border border-white/10 rounded-sm"
            title="Anzahl der Beiträge im automatischen Batch (max 15 pro Tag)"
          >
            Anzahl
            <input
              type="number"
              min={2}
              max={15}
              value={batchCount}
              onChange={(e) => setBatchCount(Math.min(15, Math.max(2, parseInt(e.target.value) || 4)))}
              data-testid="studio-batch-count"
              className="w-12 text-center bg-transparent outline-none border-none font-mono text-white"
            />
          </label>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="studio-generate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#3D2A78] hover:bg-[#2C1F5A] text-white rounded-sm disabled:opacity-50"
          >
            {generating ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <ArrowsClockwise size={14} weight="bold" />
            )}
            {generating ? "Generiere…" : `${batchCount} Stück generieren`}
          </button>
        </div>
      </header>

      <MusicLibrary />

      {/* Custom-Topic Generator — admin types own topic + chooses format */}
      <div
        className="eid-card p-4 flex flex-col gap-3"
        style={{ borderColor: "rgba(255,255,255,0.10)" }}
        data-testid="studio-custom-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-medium text-white flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" className="text-[#FF7A45]" />
              Eigenes Tagesthema
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">
              Gib ein konkretes Thema vor — die KI erstellt sofort einen einzelnen
              Beitrag. Wähle „Instagram" für eine kurze Caption + Bild,
              oder „Lang" für einen vollen 3-Absatz-Artikel.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white/5 p-1 rounded-sm">
            <button
              onClick={() => setCustomFmt("instagram")}
              data-testid="studio-fmt-instagram"
              className={`px-3 py-1.5 text-xs rounded-sm flex items-center gap-1.5 ${
                customFmt === "instagram"
                  ? "bg-gradient-to-r from-[#FFC24B] via-[#C05A3E] to-[#3D2A78] text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <InstagramLogo size={11} weight={customFmt === "instagram" ? "fill" : "regular"} />
              Instagram (kurz)
            </button>
            <button
              onClick={() => setCustomFmt("long")}
              data-testid="studio-fmt-long"
              className={`px-3 py-1.5 text-xs rounded-sm ${
                customFmt === "long"
                  ? "bg-[#F5F5F5] text-[#0A0A0A]"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              Lang (Artikel)
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !genCustom) generateCustom(); }}
            placeholder="z. B. Spätfrost an Apfelblüten in der Steiermark"
            disabled={genCustom}
            maxLength={400}
            data-testid="studio-custom-topic"
            className="flex-1 min-w-[280px] px-3 py-2 text-sm border border-white/10 rounded-sm focus:border-[#8B7CF6]/40 focus:outline-none disabled:opacity-60"
          />
          <label
            className="inline-flex items-center gap-2 px-3 py-2 text-xs border border-white/10 rounded-sm cursor-pointer hover:border-[#8B7CF6]/40 select-none"
            title="Beitrag automatisch veröffentlichen, sobald das Bild fertig ist (~15 s) — keine manuelle Freigabe nötig"
          >
            <input
              type="checkbox"
              checked={autoPublish}
              onChange={(e) => setAutoPublish(e.target.checked)}
              data-testid="studio-auto-publish"
              className="h-3.5 w-3.5 accent-[#C05A3E]"
            />
            <span className={autoPublish ? "text-[#FF7A45] font-medium" : "text-zinc-400"}>
              Sofort veröffentlichen
            </span>
          </label>
          <button
            onClick={generateCustom}
            disabled={genCustom || customTopic.trim().length < 3}
            data-testid="studio-custom-generate"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF5500] hover:bg-[#FF6A1F] text-white rounded-sm disabled:opacity-50"
          >
            {genCustom ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : (
              <Sparkle size={14} weight="fill" />
            )}
            {genCustom ? "Generiere…" : autoPublish ? "Erstellen & posten" : "Erstellen"}
          </button>
        </div>
        <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3 gap-y-1">
          <span>Beispiele:</span>
          {["Bienen-Schwarmzeit Mai", "Fruktan-Risiko bei Pferden", "Borkenkäfer Steiermark", "Trockenheit Niederösterreich"].map((ex) => (
            <button
              key={ex}
              onClick={() => setCustomTopic(ex)}
              className="hover:text-[#A99AFF] underline-offset-2 hover:underline"
            >
              {ex}
            </button>
          ))}
        </div>

        {/* Bulk-Toggle row */}
        <div className="border-t border-white/10 pt-3 mt-1 flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => setBulkOpen((v) => !v)}
            data-testid="studio-bulk-toggle"
            className="inline-flex items-center gap-1.5 text-xs text-[#A99AFF] hover:text-[#A99AFF] font-medium"
          >
            <Sparkle size={12} weight="fill" />
            {bulkOpen ? "Bulk-Modus schließen" : "Bulk-Modus: bis zu 15 Themen auf einmal"}
          </button>
          <span className="text-[11px] text-zinc-500">
            Eine Zeile = ein Beitrag · ideal für tägliches Insta-Posting
          </span>
        </div>

        {bulkOpen && (
          <div
            className="bg-[#161616] border border-white/10 rounded-sm p-3 flex flex-col gap-2.5"
            data-testid="studio-bulk-card"
          >
            <textarea
              value={bulkTopics}
              onChange={(e) => setBulkTopics(e.target.value)}
              disabled={bulkRunning}
              rows={6}
              maxLength={5000}
              placeholder={
                "Eine Zeile = ein Beitrag, z. B.:\n" +
                "Bienen-Schwarmzeit Mai\n" +
                "Fruktan-Risiko bei Pferden\n" +
                "Borkenkäfer Steiermark\n" +
                "Trockenheit Niederösterreich"
              }
              data-testid="studio-bulk-topics"
              className="w-full px-3 py-2 text-sm border border-white/10 rounded-sm focus:border-[#8B7CF6]/40 focus:outline-none disabled:opacity-60 font-mono leading-relaxed bg-[#121212]"
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-zinc-400">
                {bulkTopics.split("\n").map((s) => s.trim()).filter((s) => s.length >= 3).length}
                {" / 15 Themen"}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 bg-[#121212] p-0.5 rounded-sm border border-white/10">
                  <button
                    onClick={() => setBulkFmt("instagram")}
                    data-testid="studio-bulk-fmt-instagram"
                    className={`px-2.5 py-1 text-[11px] rounded-sm ${
                      bulkFmt === "instagram"
                        ? "bg-gradient-to-r from-[#FFC24B] to-[#3D2A78] text-white"
                        : "text-zinc-400"
                    }`}
                  >
                    Instagram (kurz)
                  </button>
                  <button
                    onClick={() => setBulkFmt("long")}
                    data-testid="studio-bulk-fmt-long"
                    className={`px-2.5 py-1 text-[11px] rounded-sm ${
                      bulkFmt === "long"
                        ? "bg-[#F5F5F5] text-[#0A0A0A]"
                        : "text-zinc-400"
                    }`}
                  >
                    Lang
                  </button>
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs text-zinc-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={bulkAuto}
                    onChange={(e) => setBulkAuto(e.target.checked)}
                    data-testid="studio-bulk-autopublish"
                    className="h-3 w-3 accent-[#C05A3E]"
                  />
                  <span className={bulkAuto ? "text-[#FF7A45] font-medium" : ""}>
                    auto-veröffentlichen
                  </span>
                </label>
                <button
                  onClick={generateBulk}
                  disabled={bulkRunning || bulkTopics.trim().length < 3}
                  data-testid="studio-bulk-generate"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#FF5500] hover:bg-[#FF6A1F] text-white rounded-sm disabled:opacity-50"
                >
                  {bulkRunning ? (
                    <CircleNotch size={12} className="animate-spin" />
                  ) : (
                    <Sparkle size={12} weight="fill" />
                  )}
                  {bulkRunning ? "Generiere…" : "Alle erstellen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="eid-card p-4 flex items-start gap-2 text-xs leading-snug"
        style={{ background: "rgba(139,124,246,0.10)", borderColor: "#C8B6F0" }}
      >
        <ShieldCheck size={16} weight="fill" className="text-[#A99AFF] shrink-0 mt-0.5" />
        <p className="text-[#A99AFF]">
          <strong>Veröffentlichungs-Hinweis:</strong> Jeder Beitrag erscheint
          öffentlich auf <code>/heute</code> mit deutlichem KI-Label
          (EU-KI-VO Art. 50) und Haftungsausschluss. Veröffentliche nichts,
          das du nicht selbst geprüft hast — inhaltliche Fehler in KI-Texten
          sind möglich.
        </p>
      </div>

      <div className="flex gap-1 p-1 rounded-sm bg-white/5 border border-white/10 inline-flex" data-testid="status-filter">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id || "all"}
            onClick={() => setStatusFilter(f.id)}
            data-testid={`filter-${f.id || "all"}`}
            className={`px-3 py-1.5 text-xs rounded-sm ${
              statusFilter === f.id
                ? "bg-[#F5F5F5] text-[#0A0A0A]"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            {f.label}
            {statusFilter === f.id && (
              <span className="ml-1.5 font-mono opacity-70">
                {posts.length}
              </span>
            )}
          </button>
        ))}
        <div className="hidden md:flex items-center text-[10px] font-mono text-zinc-500 px-3 gap-3">
          <span>{drafts} Entwürfe</span>
          <span>{published} live</span>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12 text-zinc-500">
          <CircleNotch size={28} className="animate-spin inline-block" />
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="eid-card p-10 text-center">
          <h2 className="text-lg font-medium">Keine Beiträge.</h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            Klicke auf <strong>„Jetzt generieren"</strong>, um einen ersten
            Schwung Entwürfe zu erstellen — oder warte auf den 06:00-Cron.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {posts.map((p) => (
          <PostRow key={p.id} post={p} onChange={reload} onPreview={setPreviewPost} />
        ))}
      </div>

      {previewPost && (
        <PreviewModal post={previewPost} onClose={() => setPreviewPost(null)} />
      )}
    </div>
  );
}
